/**
 * Staff Profile (Phase 4) — category-aware tabs.
 * - Common: Personal · Certifications · Evaluations · Resources
 * - Teaching adds: Assignments
 * - Casual & Volunteers adds: Hours, Background Checks (mapped to Certifications type)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ArrowLeft, Save, Camera, Award, ClipboardCheck, BookOpen, Boxes, Clock, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageToBase64 } from '../lib/imageCompress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = {
  teaching: 'Teaching',
  administrative: 'Administrative',
  casual_volunteer: 'Casual & Volunteer',
};

const StaffProfilePage = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff-mgmt/${staffId}/profile`);
      setProfile(res.data);
      setForm(res.data.staff || {});
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try {
      await axios.put(`${API_URL}/api/staff-mgmt/${staffId}`, form);
      toast.success('Saved');
      setEditing(false);
      fetch();
    } catch (e) { toast.error('Save failed'); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await compressImageToBase64(file);
      await axios.put(`${API_URL}/api/staff-mgmt/${staffId}`, { passport_photo: b64 });
      toast.success('Photo updated');
      fetch();
    } catch (err) { toast.error('Photo upload failed'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  if (!profile) return <div className="text-center py-12 text-muted-foreground">Staff not found</div>;

  const s = profile.staff;
  const fullName = s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim();
  const isTeaching = s.staff_category === 'teaching';
  const isCasual = s.staff_category === 'casual_volunteer';

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/staff')} data-testid="staff-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to list
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="pt-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="relative">
            <Avatar className="h-24 w-24 border-2 border-primary/20">
              {s.passport_photo ? (
                <img src={s.passport_photo} alt={fullName} className="h-full w-full object-cover rounded-full" />
              ) : (
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">{fullName.slice(0, 1)}</AvatarFallback>
              )}
            </Avatar>
            <label className="absolute -bottom-1 -right-1 p-1.5 bg-primary text-white rounded-full cursor-pointer hover:bg-primary/90">
              <Camera className="h-3.5 w-3.5" />
              <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="staff-photo-input" />
            </label>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold">{fullName}</h1>
            <div className="text-sm text-muted-foreground space-x-2">
              <Badge variant="outline" className="bg-primary/10 text-primary">{CATEGORY_LABEL[s.staff_category] || s.staff_category}</Badge>
              {s.job_title && <span>· {s.job_title}</span>}
              {s.department && <span>· {s.department}</span>}
            </div>
            {profile.stats.certs_expired > 0 && (
              <Badge variant="outline" className="mt-2 bg-red-50 text-red-700 border-red-200">
                <AlertTriangle className="h-3 w-3 mr-1 inline" /> {profile.stats.certs_expired} expired certification{profile.stats.certs_expired !== 1 && 's'}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">{profile.stats.certs_total}</div>
              <div className="text-xs text-muted-foreground">Certs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{profile.stats.evaluations_count}</div>
              <div className="text-xs text-muted-foreground">Evaluations</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{isCasual ? profile.stats.volunteer_hours_total : profile.assignments.length}</div>
              <div className="text-xs text-muted-foreground">{isCasual ? 'Hours' : 'Assignments'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="personal">
        <TabsList className="flex flex-wrap w-full md:w-auto">
          <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="certs" data-testid="tab-certs"><Award className="h-3.5 w-3.5 mr-1" />Certifications</TabsTrigger>
          <TabsTrigger value="evals" data-testid="tab-evals"><ClipboardCheck className="h-3.5 w-3.5 mr-1" />Evaluations</TabsTrigger>
          {isTeaching && <TabsTrigger value="assignments" data-testid="tab-assignments"><BookOpen className="h-3.5 w-3.5 mr-1" />Assignments</TabsTrigger>}
          <TabsTrigger value="resources" data-testid="tab-resources"><Boxes className="h-3.5 w-3.5 mr-1" />Resources</TabsTrigger>
          {isCasual && <TabsTrigger value="hours" data-testid="tab-hours"><Clock className="h-3.5 w-3.5 mr-1" />Hours</TabsTrigger>}
        </TabsList>

        <TabsContent value="personal" className="mt-4">
          <PersonalTab s={s} form={form} setForm={setForm} editing={editing} setEditing={setEditing} save={save} />
        </TabsContent>

        <TabsContent value="certs" className="mt-4">
          <CertsTab staffId={staffId} certs={profile.certifications} onChange={fetch} />
        </TabsContent>

        <TabsContent value="evals" className="mt-4">
          <EvalsTab staffId={staffId} evals={profile.evaluations} onChange={fetch} />
        </TabsContent>

        {isTeaching && (
          <TabsContent value="assignments" className="mt-4">
            <AssignmentsTab staffId={staffId} assignments={profile.assignments} onChange={fetch} />
          </TabsContent>
        )}

        <TabsContent value="resources" className="mt-4">
          <ResourcesTab staffId={staffId} resources={profile.resources} onChange={fetch} />
        </TabsContent>

        {isCasual && (
          <TabsContent value="hours" className="mt-4">
            <HoursTab staffId={staffId} totalHours={profile.stats.volunteer_hours_total} onChange={fetch} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// ============== Editable field ==============
const Field = ({ label, name, form, setForm, editing, type = 'text', options }) => {
  const value = form[name] ?? '';
  if (!editing) return <div><div className="text-xs text-muted-foreground">{label}</div><div className="text-sm font-medium">{Array.isArray(value) ? (value.join(', ') || '—') : (value || '—')}</div></div>;
  if (type === 'select') return (
    <div><Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={(v) => setForm({ ...form, [name]: v })}>
        <SelectTrigger data-testid={`field-${name}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (type === 'textarea') return <div><Label className="text-xs">{label}</Label><Textarea value={value} onChange={(e) => setForm({ ...form, [name]: e.target.value })} data-testid={`field-${name}`} /></div>;
  if (type === 'array') return (
    <div><Label className="text-xs">{label} <span className="text-muted-foreground">(comma-separated)</span></Label>
      <Input value={Array.isArray(value) ? value.join(', ') : value} onChange={(e) => setForm({ ...form, [name]: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} data-testid={`field-${name}`} />
    </div>
  );
  return <div><Label className="text-xs">{label}</Label><Input type={type} value={value} onChange={(e) => setForm({ ...form, [name]: type === 'number' ? parseFloat(e.target.value) : e.target.value })} data-testid={`field-${name}`} /></div>;
};

const PersonalTab = ({ s, form, setForm, editing, setEditing, save }) => {
  const isTeaching = s.staff_category === 'teaching';
  const isAdmin = s.staff_category === 'administrative';
  const isCasual = s.staff_category === 'casual_volunteer';
  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="flex justify-between">
          <h3 className="font-semibold">Personal Information</h3>
          {editing ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={save} data-testid="save-staff-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
            </div>
          ) : <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-staff-btn">Edit</Button>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Category" name="staff_category" form={form} setForm={setForm} editing={editing} type="select"
            options={[{ value: 'teaching', label: 'Teaching' }, { value: 'administrative', label: 'Administrative' }, { value: 'casual_volunteer', label: 'Casual & Volunteer' }]} />
          <Field label="First Name" name="first_name" form={form} setForm={setForm} editing={editing} />
          <Field label="Last Name" name="last_name" form={form} setForm={setForm} editing={editing} />
          <Field label="Gender" name="gender" form={form} setForm={setForm} editing={editing} type="select"
            options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} />
          <Field label="Date of Birth" name="date_of_birth" form={form} setForm={setForm} editing={editing} type="date" />
          <Field label="National ID" name="national_id" form={form} setForm={setForm} editing={editing} />
          <Field label="Phone" name="phone" form={form} setForm={setForm} editing={editing} />
          <Field label="Email" name="email" form={form} setForm={setForm} editing={editing} type="email" />
          <Field label="Home Address" name="home_address" form={form} setForm={setForm} editing={editing} />
        </div>

        <div>
          <h4 className="font-semibold mb-3">Emergency Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Contact Name" name="emergency_contact_name" form={form} setForm={setForm} editing={editing} />
            <Field label="Contact Phone" name="emergency_contact_phone" form={form} setForm={setForm} editing={editing} />
            <Field label="Contact Address" name="emergency_contact_address" form={form} setForm={setForm} editing={editing} />
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Employment</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Status" name="employment_status" form={form} setForm={setForm} editing={editing} type="select"
              options={[{ value: 'active', label: 'Active' }, { value: 'on_leave', label: 'On Leave' }, { value: 'terminated', label: 'Terminated' }]} />
            <Field label="Type" name="employment_type" form={form} setForm={setForm} editing={editing} type="select"
              options={[{ value: 'full_time', label: 'Full Time' }, { value: 'part_time', label: 'Part Time' }, { value: 'contract', label: 'Contract' }]} />
            <Field label="Start Date" name="start_date" form={form} setForm={setForm} editing={editing} type="date" />
            <Field label="End Date" name="end_date" form={form} setForm={setForm} editing={editing} type="date" />
            <Field label="Salary (UGX)" name="salary" form={form} setForm={setForm} editing={editing} type="number" />
          </div>
        </div>

        {isTeaching && (
          <div>
            <h4 className="font-semibold mb-3">Teaching Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Subjects" name="subjects" form={form} setForm={setForm} editing={editing} type="array" />
              <Field label="Grade Levels" name="grade_levels" form={form} setForm={setForm} editing={editing} type="array" />
              <Field label="Homeroom Class" name="homeroom_class" form={form} setForm={setForm} editing={editing} />
              <Field label="Room Number" name="room_number" form={form} setForm={setForm} editing={editing} />
              <Field label="Teaching License #" name="license_number" form={form} setForm={setForm} editing={editing} />
            </div>
          </div>
        )}

        {isAdmin && (
          <div>
            <h4 className="font-semibold mb-3">Administrative Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Department" name="department" form={form} setForm={setForm} editing={editing} />
              <Field label="Job Title" name="job_title" form={form} setForm={setForm} editing={editing} />
              <Field label="Reports To (staff id)" name="reporting_to" form={form} setForm={setForm} editing={editing} />
              <Field label="Work Location" name="work_location" form={form} setForm={setForm} editing={editing} />
              <Field label="Desk Extension" name="desk_extension" form={form} setForm={setForm} editing={editing} />
            </div>
          </div>
        )}

        {isCasual && (
          <div>
            <h4 className="font-semibold mb-3">Casual / Volunteer Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Contract From" name="contract_period_from" form={form} setForm={setForm} editing={editing} type="date" />
              <Field label="Contract To" name="contract_period_to" form={form} setForm={setForm} editing={editing} type="date" />
              <Field label="Availability" name="availability_schedule" form={form} setForm={setForm} editing={editing} />
              <Field label="Background Check Status" name="background_check_status" form={form} setForm={setForm} editing={editing} type="select"
                options={[{ value: 'pending', label: 'Pending' }, { value: 'cleared', label: 'Cleared' }, { value: 'flagged', label: 'Flagged' }]} />
              <Field label="Background Check Date" name="background_check_date" form={form} setForm={setForm} editing={editing} type="date" />
              <Field label="Fingerprint Status" name="fingerprint_status" form={form} setForm={setForm} editing={editing} />
              <Field label="Clearance Expiry" name="clearance_expiry" form={form} setForm={setForm} editing={editing} type="date" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Certifications ==============
const CertsTab = ({ staffId, certs, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ cert_type: 'teaching_license', cert_name: '', issuing_authority: '', cert_number: '', issue_date: '', expiry_date: '' });

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/staff-mgmt/certifications`, { ...form, staff_id: staffId });
      toast.success('Added'); setAdding(false); onChange();
      setForm({ cert_type: 'teaching_license', cert_name: '', issuing_authority: '', cert_number: '', issue_date: '', expiry_date: '' });
    } catch (e) { toast.error('Add failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/staff-mgmt/certifications/${id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Card><CardContent className="pt-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Certifications & Licenses</h3>
        <Button size="sm" onClick={() => setAdding(true)} data-testid="add-cert-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Name</TableHead><TableHead>Cert #</TableHead><TableHead>Issuer</TableHead><TableHead>Expiry</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {certs.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4">None on file</TableCell></TableRow>
          : certs.map((c) => {
            const expired = c.expiry_date && c.expiry_date < today;
            const expiringSoon = c.expiry_date && c.expiry_date >= today && c.expiry_date <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
            return (
              <TableRow key={c.id}>
                <TableCell><Badge variant="outline" className="capitalize">{(c.cert_type || '').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell>{c.cert_name}</TableCell>
                <TableCell className="font-mono text-xs">{c.cert_number || '—'}</TableCell>
                <TableCell className="text-sm">{c.issuing_authority || '—'}</TableCell>
                <TableCell>
                  {c.expiry_date ? (
                    <Badge variant="outline" className={expired ? 'bg-red-50 text-red-700 border-red-200' : expiringSoon ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-green-50 text-green-700 border-green-200'}>
                      {expired ? 'Expired ' : expiringSoon ? 'Expires ' : 'Valid until '}{c.expiry_date}
                    </Badge>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-cert-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Certification</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.cert_type} onValueChange={(v) => setForm({ ...form, cert_type: v })}>
                <SelectTrigger data-testid="cert-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teaching_license">Teaching License</SelectItem>
                  <SelectItem value="first_aid">First Aid</SelectItem>
                  <SelectItem value="hr_certification">HR Certification</SelectItem>
                  <SelectItem value="background_check">Background Check / CORI</SelectItem>
                  <SelectItem value="fingerprint">Fingerprint Clearance</SelectItem>
                  <SelectItem value="principal_license">Principal License</SelectItem>
                  <SelectItem value="notary">Notary</SelectItem>
                  <SelectItem value="financial_compliance">Financial Compliance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input value={form.cert_name} onChange={(e) => setForm({ ...form, cert_name: e.target.value })} data-testid="cert-name" /></div>
            <div><Label>Cert Number</Label><Input value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} /></div>
            <div><Label>Issuing Authority</Label><Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} /></div>
            <div><Label>Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
            <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="cert-expiry" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="cert-submit-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ============== Evaluations ==============
const EvalsTab = ({ staffId, evals, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ evaluation_type: 'observation', date: '', evaluator: '', score: '', summary: '', next_action: '' });

  const submit = async () => {
    try {
      const body = { ...form, staff_id: staffId, score: form.score ? parseFloat(form.score) : null };
      await axios.post(`${API_URL}/api/staff-mgmt/evaluations`, body);
      toast.success('Added'); setAdding(false); onChange();
      setForm({ evaluation_type: 'observation', date: '', evaluator: '', score: '', summary: '', next_action: '' });
    } catch (e) { toast.error('Add failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/staff-mgmt/evaluations/${id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Card><CardContent className="pt-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Evaluations</h3>
        <Button size="sm" onClick={() => setAdding(true)} data-testid="add-eval-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Evaluator</TableHead><TableHead>Score</TableHead><TableHead>Summary</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {evals.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4">None on file</TableCell></TableRow>
          : evals.map((e) => (
            <TableRow key={e.id}>
              <TableCell><Badge variant="outline" className="capitalize">{(e.evaluation_type || '').replace(/_/g, ' ')}</Badge></TableCell>
              <TableCell className="text-xs">{e.date}</TableCell>
              <TableCell>{e.evaluator || '—'}</TableCell>
              <TableCell>{e.score != null ? <Badge variant="outline">{e.score}/5</Badge> : '—'}</TableCell>
              <TableCell className="text-xs max-w-md truncate" title={e.summary}>{e.summary || '—'}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => del(e.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Evaluation</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.evaluation_type} onValueChange={(v) => setForm({ ...form, evaluation_type: v })}>
                <SelectTrigger data-testid="eval-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="observation">Observation</SelectItem>
                  <SelectItem value="annual_review">Annual Review</SelectItem>
                  <SelectItem value="professional_development">Professional Development</SelectItem>
                  <SelectItem value="disciplinary">Disciplinary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="eval-date" /></div>
            <div><Label>Evaluator</Label><Input value={form.evaluator} onChange={(e) => setForm({ ...form, evaluator: e.target.value })} /></div>
            <div><Label>Score (1-5)</Label><Input type="number" min="1" max="5" step="0.1" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} /></div>
            <div className="col-span-2"><Label>Summary</Label><Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="eval-summary" /></div>
            <div className="col-span-2"><Label>Next Action</Label><Textarea value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="eval-submit-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ============== Assignments (teaching) ==============
const AssignmentsTab = ({ staffId, assignments, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: 'subject', name: '', grade_level: '', room: '', schedule: '', term: '' });

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/staff-mgmt/assignments`, { ...form, staff_id: staffId });
      toast.success('Added'); setAdding(false); onChange();
      setForm({ type: 'subject', name: '', grade_level: '', room: '', schedule: '', term: '' });
    } catch (e) { toast.error('Add failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/staff-mgmt/assignments/${id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Card><CardContent className="pt-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Teaching Assignments</h3>
        <Button size="sm" onClick={() => setAdding(true)} data-testid="add-assignment-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Name</TableHead><TableHead>Grade</TableHead><TableHead>Room</TableHead><TableHead>Schedule</TableHead><TableHead>Term</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {assignments.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-4">None</TableCell></TableRow>
          : assignments.map((a) => (
            <TableRow key={a.id}>
              <TableCell><Badge variant="outline" className="capitalize">{a.type}</Badge></TableCell>
              <TableCell>{a.name}</TableCell>
              <TableCell>{a.grade_level || '—'}</TableCell>
              <TableCell>{a.room || '—'}</TableCell>
              <TableCell className="text-xs">{a.schedule || '—'}</TableCell>
              <TableCell className="text-xs">{a.term || '—'}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => del(a.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Assignment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">Subject</SelectItem>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="homeroom">Homeroom</SelectItem>
                  <SelectItem value="duty">Duty</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="assignment-name" /></div>
            <div><Label>Grade Level</Label><Input value={form.grade_level} onChange={(e) => setForm({ ...form, grade_level: e.target.value })} /></div>
            <div><Label>Room</Label><Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
            <div><Label>Schedule</Label><Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Mon-Fri 9:00-10:00" /></div>
            <div><Label>Term</Label><Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="Term 1 2026" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="assignment-submit-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ============== Resources ==============
const ResourcesTab = ({ staffId, resources, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ resource_type: 'laptop', name: '', serial_number: '', issued_date: '', condition: '' });

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/staff-mgmt/resources`, { ...form, staff_id: staffId });
      toast.success('Issued'); setAdding(false); onChange();
      setForm({ resource_type: 'laptop', name: '', serial_number: '', issued_date: '', condition: '' });
    } catch (e) { toast.error('Failed'); }
  };
  const ret = async (id) => {
    try { await axios.post(`${API_URL}/api/staff-mgmt/resources/${id}/return`); toast.success('Returned'); onChange(); }
    catch (e) { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/staff-mgmt/resources/${id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <Card><CardContent className="pt-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Issued Resources</h3>
        <Button size="sm" onClick={() => setAdding(true)} data-testid="add-resource-btn"><Plus className="h-4 w-4 mr-1" />Issue</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Name</TableHead><TableHead>Serial</TableHead><TableHead>Issued</TableHead><TableHead>Returned</TableHead><TableHead>Condition</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {resources.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-4">None</TableCell></TableRow>
          : resources.map((r) => (
            <TableRow key={r.id} className={r.returned_date ? 'opacity-60' : ''}>
              <TableCell><Badge variant="outline" className="capitalize">{(r.resource_type || '').replace(/_/g, ' ')}</Badge></TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell className="font-mono text-xs">{r.serial_number || '—'}</TableCell>
              <TableCell className="text-xs">{r.issued_date}</TableCell>
              <TableCell className="text-xs">{r.returned_date || <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">In use</Badge>}</TableCell>
              <TableCell>{r.condition || '—'}</TableCell>
              <TableCell className="space-x-1">
                {!r.returned_date && <Button size="sm" variant="outline" onClick={() => ret(r.id)} data-testid={`return-${r.id}`}>Return</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Resource</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.resource_type} onValueChange={(v) => setForm({ ...form, resource_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laptop">Laptop</SelectItem>
                  <SelectItem value="classroom_key">Classroom Key</SelectItem>
                  <SelectItem value="access_card">Access Card</SelectItem>
                  <SelectItem value="textbook_set">Textbook Set</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resource-name" /></div>
            <div><Label>Serial #</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
            <div><Label>Issued Date</Label><Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} data-testid="resource-issued" /></div>
            <div className="col-span-2"><Label>Condition</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} placeholder="New / Good / Fair" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="resource-submit-btn">Issue</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ============== Hours (volunteers) ==============
const HoursTab = ({ staffId, totalHours, onChange }) => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: '', hours: '', activity: '', location: '' });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff-mgmt/${staffId}/hours`);
      setItems(res.data.items || []);
    } catch (e) { toast.error('Failed to load hours'); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { fetch(); }, [fetch]);

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/staff-mgmt/hours`, { ...form, staff_id: staffId, hours: parseFloat(form.hours) });
      toast.success('Logged'); setAdding(false); onChange(); fetch();
      setForm({ date: '', hours: '', activity: '', location: '' });
    } catch (e) { toast.error('Add failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/staff-mgmt/hours/${id}`); toast.success('Deleted'); onChange(); fetch(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Card><CardContent className="pt-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Volunteer Hours <Badge variant="outline" className="ml-2 bg-primary/10 text-primary">{totalHours} total</Badge></h3>
        <Button size="sm" onClick={() => setAdding(true)} data-testid="add-hours-btn"><Plus className="h-4 w-4 mr-1" />Log Hours</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Activity</TableHead><TableHead>Location</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground">Loading…</TableCell></TableRow>
          : items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground">No hours logged</TableCell></TableRow>
          : items.map((h) => (
            <TableRow key={h.id}>
              <TableCell className="text-xs">{h.date}</TableCell>
              <TableCell className="text-right tabular-nums font-medium">{h.hours}</TableCell>
              <TableCell>{h.activity || '—'}</TableCell>
              <TableCell className="text-xs">{h.location || '—'}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => del(h.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Hours</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="hours-date" /></div>
            <div><Label>Hours</Label><Input type="number" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="hours-amount" /></div>
            <div className="col-span-2"><Label>Activity</Label><Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} /></div>
            <div className="col-span-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="hours-submit-btn">Log</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

export default StaffProfilePage;
